/**
 * PresentDetailContainer
 * --
 */
import React, { useEffect } from 'react';
import PresentDetailPresenter from './PresentDetailPresenter';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  useGetPresentGuest,
  useUpdatePresent,
  useUpdateAmountPresent,
} from 'hooks/PresentHooks';
import { useManualChargeJmPay } from 'hooks/JmPayHooks';
import dayjs from 'dayjs';
import MessageAlert, {
  stringToMoneyFormat,
  HOST_URL,
  PAYMENT_STORE_ID,
  PAYMENT_PC_URL,
  jsonToQueryString,
  DOMAIN_URL,
  DEPLOY_TYPE,
  isMobile,
  PAYMENT_URL,
} from 'utils';
import { useFileDownload } from 'hooks/FileHooks';
import { useGetForwardingLocations } from 'hooks/ForwardingLocationsHooks';
import { useCreateShopOrder } from 'hooks/ShopOrderHooks';
import Formatter from 'utils/Formatter';
// import { usePaymentCancelCard } from 'hooks/PaymentHooks.';
import { useGetPresentCard } from 'hooks/PresentCardHooks';
import { useCreateTempPayment } from 'hooks/TempHooks';
import Logger from 'utils/logger';

const logger = new Logger('PresentDetail');

const PresentDetailContainer = (props) => {
  /* ===== ROUTE ===== */
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const serial = searchParams.get('serial');

  const payData = searchParams.get('pay') ?? null;

  /* ===== PROPS ===== */

  /* ===== Data ===== */
  // 선물받은 데이터
  const { data: PresentData } = useGetPresentGuest(
    {
      barcode: params?.presentId,
      serial: serial,
    },
    {
      enabled: params?.presentId && serial ? true : false,
      select: (res) => {
        const { status, message, data } = res;

        if (status !== 200) {
          console.warn(message);
          return null;
        }

        return data;
      },
      initialData: null,
    }
  );
  // 출고지
  const { data: Forwarding } = useGetForwardingLocations(null, {
    enabled:
      PresentData && PresentData.product.is_store_pickup === 'Y' ? true : false,
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      const filter = data.filter((forward) => forward.type === 'RECEIVE');

      return filter;
    },
    initialData: [],
  });
  // 선물카드
  const { data: Card } = useGetPresentCard(PresentData?.present_card_id, null, {
    enabled: !!PresentData?.present_card_id,
    initialData: null,
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return null;

      return data.image_url;
    },
  });

  /* ===== Mutate ===== */
  const { fileDownload } = useFileDownload();
  const { manualChargeJmPay } = useManualChargeJmPay();

  const { createShopOrder } = useCreateShopOrder();
  // const { cancelCard } = usePaymentCancelCard();
  const { updatePresent } = useUpdatePresent();
  const { updateAmountPresent } = useUpdateAmountPresent();

  const { createTempPayment } = useCreateTempPayment();

  /* ===== STATE ===== */
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  // 입력값
  const [inputValue, setInputValue] = React.useState({});
  // 선택 픽업지
  const [selected, setSelected] = React.useState(null);
  // 결제 진행여부
  const [isProgress, setIsProgress] = React.useState(false);
  // 결제 리턴값
  const [paymentReturn, setPaymentReturn] = React.useState(null);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 파일 이미지 다운로드
   * --
   * @param {string} url
   * @param {string} name
   * @returns
   */
  const handleDownload = async (url, name) => {
    if (!url) {
      MessageAlert.info('다운로드에 실패하였습니다.');
      return;
    }

    try {
      setIsLoading(true);

      const { status, data } = await fileDownload({
        type: 'buffer',
        file_name: url,
      });

      if (status !== 200) {
        MessageAlert.info('다운로드에 실패하였습니다.');
        return;
      }

      const blob = new Blob([new Uint8Array(data.data)], {
        type: 'image/jpeg',
      });

      const objectURL = URL.createObjectURL(blob);

      // 모바일 환경에서는 window.open 사용
      if (isMobile()) {
        const isNative = window.ReactNativeWebView;

        if (isNative) {
          const reader = new FileReader();

          reader.onloadend = function () {
            const base64data = reader.result;

            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                type: 'DOWNLOAD_IMAGE',
                blob: base64data,
              })
            );
          };

          reader.readAsDataURL(blob);
        } else {
          window.open(objectURL, '_blank');
        }
      } else {
        const link = document.createElement('a');
        link.href = objectURL;
        link.download = `${name}.jpeg`;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
      }

      URL.revokeObjectURL(objectURL);
    } catch (err) {
      logger.error('Error!!', err);
      MessageAlert.error('다운로드중 오류가 발생하였습니다.');
    } finally {
      setTimeout(() => setIsLoading(false), 1000);
    }
  };

  /**
   * 데이터 바인딩
   * --
   * @param {string} t
   * @param {string} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**
   * 유효성 검사
   * --
   * @param {string} t
   */
  const handleValidateCheck = (t = 'DELIVERY') => {
    switch (t) {
      case 'DELIVERY':
        const {
          recipient_name,
          recipient_phone,
          recipient_postcode,
          recipient_address,
        } = inputValue;

        if (!recipient_name) {
          MessageAlert.info('받는 분 성함을 입력해주세요!');
          return false;
        }

        if (!recipient_phone) {
          MessageAlert.info('받는 분 연락처를 입력해주세요!');
          return false;
        }

        if (!recipient_postcode || !recipient_address) {
          MessageAlert.info('받는 분 주소를 입력해주세요!');
          return false;
        }

        return true;
      case 'STORE_PICKUP':
        if (!selected) {
          MessageAlert.info('픽업지를 선택해주세요!');
          return false;
        }

        return true;
      default:
        return false;
    }
  };

  /**
   * 선물하기 결제
   * --
   * @param {object} order
   * @param {number} price
   */
  const handlePayment = async (order, price) => {
    try {
      const mobileCheck = isMobile();

      const { status, data, message } = await createTempPayment({
        order_info: order,
        type: 'SHOP_ORDER',
      });

      if (status !== 200) {
        logger.error('Temp Error!!', { status, data, message });
        MessageAlert.error('상품주문에 실패하였습니다!');
        return;
      }

      const paymentData = {
        sndPaymethod: '1000000000000',
        sndStoreid: PAYMENT_STORE_ID,
        sndOrdernumber: `JM_${Formatter.dateToUnix()}`,
        sndGoodname: `배송비`,
        sndAmount: `${price}`,
        sndOrdername: `${inputValue?.recipient_name}`,
        iframeYn: 'N',
        sndCurrencytype: 'WON',
        sndShowcard: 'C',
        sndCharSet: 'utf-8',
        sndReply: `${HOST_URL}/payment/kspay/rcv`,
        sndStoreCeoName: '양재석',
        sndStorePhoneNo: '010-5138-5511',
        sndStoreAddress: '서울 강남구 도산대로11길 15 1~4층',

        tempId: data.temp_id,

        ...(mobileCheck
          ? {
              path: location.pathname,
              searchParams: location.search,
              // orderData: JSON.stringify({ data: data, inputValue: inputValue }),
            }
          : {}),
      };

      // sessionStorage.setItem(
      //   'presentData',
      //   JSON.stringify({ data: Present, inputValue: inputValue })
      // );

      let paymentURL = '';
      if (mobileCheck) {
        paymentURL = PAYMENT_URL;
      } else {
        paymentURL = PAYMENT_PC_URL;
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentURL;
      form.target = '_self';

      Object.entries(paymentData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden'; // 사용자에게는 보이지 않도록 hidden으로 설정
        input.name = key; // input의 name 속성은 객체의 key
        input.value = value; // input의 value 속성은 객체의 value
        form.appendChild(input);
      });

      form.sndReply.value = `${form.sndReply.value}${jsonToQueryString(
        paymentData
      )}`;

      document.body.appendChild(form);

      if (mobileCheck) {
        form.submit();
      } else {
        window._pay(form);
      }

      setIsProgress(true);
    } catch (err) {
      console.error('[handlePresent] Error!! ', err);
      MessageAlert.error('에러가 발생하였습니다.');
    }
  };
  /**
   * 금액권 수정
   * 수정이 안 되었을 경우 제이엠페이 충전 x
   */
  const handleAmountCoupons = async (present) => {
    try {
      const { status } = await updateAmountPresent({
        ...present,
        num: present?.product?.num,
        price: present?.product?.price,
      });
      return status;
    } catch (e) {
      console.error('[PresentDetailContainer][handleAmountCoupons]', e);
    }
  };

  /**
   * 주문하기
   * --
   */
  const handleSubmit = async () => {
    /**
     * 금액권일 경우 제이엠페이로 충전
     * @API (/change/manual)
     */
    console.log(Present, '<<<');
    if (Present?.product?.is_amount === 'Y') {
      try {
        /**
         * 금액권 사용처리
         */
        const amountStatus = await handleAmountCoupons(Present);
        if (amountStatus !== 200) {
          MessageAlert.warning('JM페이 충전에 실패하였습니다.');
          return;
        }
        /**
         * 제이엠페이 충전 로직
         */
        // const { status, data } = await manualChargeJmPay({
        //   user_id: Present?.target_id,
        //   price: Present?.product?.price,
        //   payment_info: {
        //     ...Present,
        //   },
        // });

        // if (status !== 200) {
        //   MessageAlert.warning('JM페이 충전에 실패하였습니다.');
        //   return;
        // }

        window.opener?.postMessage(
          {
            data: JSON.stringify(data),
            source: 'close',
          },
          '*'
        );

        MessageAlert.success('성공적으로 충전되었습니다.');
        setTimeout(() => {
          setIsLoading(false);
          window.close();
        }, 500);
        return;
      } catch (err) {
        console.error('[handlePaymentSubmit] Error!! : ', err);
        MessageAlert.error('상품 주문에 실패하였습니다!');
      }
    }
    if (!handleValidateCheck(Present?.shipping_type)) return;

    try {
      // 배송비 결제 처리
      if (
        Present?.shipping_type === 'DELIVERY' &&
        Present?.final_delivery_price
      ) {
        const confirm = window.confirm(
          `배송비 ${stringToMoneyFormat(
            Present?.final_delivery_price
          )}원을 결제하시겠습니까?`
        );

        if (!confirm) return;

        // await handlePayment(Present?.final_delivery_price);
        handleCreate(Present?.final_delivery_price);
        return;
      }

      handleCreate();
    } catch (err) {
      logger.error(`[handleSubmit] Error!! : `, err);
      MessageAlert.error('결제에 실패하였습니다!');
    }
  };

  /**
   * 오더 생성
   * --
   * @param {null | number} pay
   * @returns
   */
  const handleCreate = async (pay = null) => {
    let isSuccess = false;

    try {
      setIsLoading(true);

      const { product, ...rest } = data;
      const { receive_ids, ...productRest } = product;

      const orderData = {
        user_id: rest?.target_id,
        payment_method: 'PRESENT',
        payment_state: 'COMPLETED',
        product_name: productRest?.product_name,
        total_product_count: rest?.received_present - 1,
        shop_information_id: productRest?.shop_information_id,

        shop_order: [
          {
            shop_information_id: productRest?.shop_information_id,
            final_discount: -rest?.payment_price,
            final_reward_point: 0,
            final_price:
              rest?.shipping_type === 'DELIVERY'
                ? rest?.final_delivery_price ?? 0
                : 0, // 배송비
            final_delivery_price:
              (rest?.delivery_price ?? 0) + (rest?.final_delivery_price ?? 0),
            payment_method: 'PRESENT',
            payment_state: 'COMPLETED',
            order_route: 'PC',
            // payment_info: pay, // 배송비 결제 정보
            payment_info: null, // 배송비 결제 정보
            use_point: 0,
            use_jm_pay: 0,
            delivery_address:
              rest?.shipping_type === 'DELIVERY' ? inputValue : selected,
            forwarding_location_id:
              rest?.shipping_type === 'DELIVERY'
                ? product.forwarding_location_id
                : selected.forwarding_location_id,
            // NOTE: 배송 선물은 현금영수증 발행 X
            cash_bill_info: null, // 현금영수증
            is_cashbill: 'N', // 현금영수증 발행 여부
            type: rest?.shipping_type,
            product_information: [
              {
                ...productRest,
                ...rest,
                payment_price: 0,
                discount_price: -(rest?.payment_price - rest?.delivery_price),
                reserve_price: 0,
                delivery_date: null,
                regular_updated_at: null,
                rental_price: null,
                card_id: null,
                present_type: rest?.type,
                gift_type: rest?.present_type,
                num: rest?.received_present,
              },
            ],
          },
        ],
      };

      // 배송비 결제가 있을 경우
      if (pay) {
        await handlePayment(orderData, pay);
        return;
      }
      // 배송비 결제가 없을 경우 오더 생성
      else {
        const { status, message, data } = await createShopOrder(orderData);

        if (status !== 200) {
          MessageAlert.warning('상품 주문에 실패하였습니다!');
          logger.error('Create Shop Order Error!!', { status, message, data });
          return;
        }
      }

      MessageAlert.success('상품이 성공적으로 주문되었습니다!');
      isSuccess = true;
    } catch (err) {
      logger.error('선물하기 사용 에러', err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        if (isSuccess) {
          window.location.href = `/common/present/${params?.presentId}?serial=${serial}`;
          // window.location.reload();
        }
      }, 1000);
    }
  };

  /**
   * 선물하기 기간 연장
   * --
   * @param {number} idx
   * @param {number} exp
   * @param {number} date
   * @param {number} count
   */
  const handleExtension = async (
    idx = null,
    exp = null,
    date = null,
    count
  ) => {
    if (!idx || !date || !exp || !count) {
      MessageAlert.info('연장할 수 없는 상품입니다.');
      return;
    }

    const confirm = window.confirm(`${date}일 만큼 연장하시겠습니까?`);

    if (!confirm) return;

    try {
      setIsLoading(true);

      const expDate = dayjs.unix(exp).add(date, 'day').unix();

      const { status } = await updatePresent({
        present_id: idx,
        expired_date: expDate,
        extendable_count: --count,
      });

      if (status !== 200) {
        MessageAlert.info('선물 기간 연장에 실패하였습니다.');
        return;
      }

      MessageAlert.success(
        `유효기간이 ${dayjs
          .unix(expDate)
          .format('YYYY년 MM월 DD일')}까지 연장되었습니다.`
      );

      setData((prev) => ({
        ...prev,
        expired_date: expDate,
        extendable_count: --count,
      }));
    } catch (err) {
      logger.error('Error!!', err);
      MessageAlert.error('선물 기간 연장에 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  const today = React.useMemo(() => {
    return dayjs().unix();
  }, []);

  /**
   * 선물 데이터 정제
   * --
   */
  const Present = React.useMemo(() => {
    if (!PresentData) return null;

    const { use_date, expired_date, product, type } = PresentData;

    const { receive_ids, forwarding_location, is_amount } = product;

    let remoteFee = 0;

    if (type === 'PRODUCT' && inputValue && is_amount === 'N') {
      // 배송비 계산
      const { remote_area_postcode } = forwarding_location;

      const { recipient_postcode } = inputValue;

      const findRemoteArea = remote_area_postcode?.find(
        (remote) =>
          remote.range[0] <= recipient_postcode &&
          remote.range[1] >= recipient_postcode
      );

      if (findRemoteArea) {
        remoteFee = findRemoteArea.price;
      }
    }
    const finalDeliveryPrice = remoteFee;

    const newPresent = {
      ...PresentData,
      status: use_date ? 'USED' : today > expired_date ? 'EXPIRED' : 'USEABLE',
      final_delivery_price: finalDeliveryPrice,
      product: {
        ...product,
        receive_ids:
          receive_ids && receive_ids.length
            ? receive_ids.map((receive) => {
                const findForwarding = Forwarding.find(
                  (forward) =>
                    forward.forwarding_location_id ===
                    receive.forwarding_location_id
                );

                return {
                  ...receive,
                  ...findForwarding,
                };
              })
            : [],
      },
    };

    return newPresent;
  }, [PresentData, today, Forwarding, inputValue]);

  /**
   * 팝업 메세지 감지
   * --
   */
  React.useEffect(() => {
    // 메시지 수신 핸들러
    const handleMessage = (event) => {
      const allowedOrigins = [
        `http://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `http://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
        `https://mall-jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
      ];

      // 보안 체크: 같은 origin에서 온 메시지만 허용
      if (!allowedOrigins.includes(event.origin)) {
        console.warn('허용되지 않은 origin:', event.origin);
        return;
      }

      const message = event.data;

      // 팝업에서 보낸 데이터만 처리
      // NOTE: 결제 성공
      if (message?.source === 'payment-success') {
        const parseData = JSON.parse(message?.data);
        window.CloseEvent();

        setPaymentReturn({
          status: 'success',
          data: parseData,
        });
        setIsProgress(false);
      }
      // NOTE: 결제 실패
      else if (message?.source === 'payment-fail') {
        const parseData = JSON.parse(message?.data);
        window.CloseEvent();

        setPaymentReturn({
          status: 'fail',
          data: parseData,
        });
      }
    };

    window.addEventListener('message', handleMessage);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  /**
   * 결제 성공 데이터 감지
   * --
   */
  React.useEffect(() => {
    if (!paymentReturn) return;

    const {
      status,
      //  data
    } = paymentReturn;

    // 결제 실패 시
    if (status !== 'success') {
      MessageAlert.error('결제에 실패하였습니다!');

      setIsProgress(false);
      setIsLoading(false);
      setPaymentReturn(null);
    } else {
      setIsProgress(false);
      setPaymentReturn(null);

      MessageAlert.success('상품이 성공적으로 주문되었습니다!');
      setTimeout(() => {
        setIsLoading(false);

        // window.location.reload();
        window.location.href = `/common/present/${params?.presentId}?serial=${serial}`;
      }, 1000);
    }
    // eslint-disable-next-line
  }, [paymentReturn]);

  /**
   * 결제 팝업 상태 확인
   * --
   */
  React.useEffect(() => {
    let popupCheck;

    if (isProgress) {
      // 1초마다 팝업 상태 확인
      popupCheck = setInterval(() => {
        if (window._kspay_pop_obj?.closed) {
          window.closeIframe();
          setIsProgress(false);
          clearInterval(popupCheck); // 팝업이 닫히면 타이머 종료
        } else {
          return;
        }
      }, 1000);
    }

    return () => {
      if (popupCheck) {
        clearInterval(popupCheck);
      }
    };
  }, [isProgress]);

  /**
   * 앱 결제 후 선물 생성 처리
   * --
   */
  React.useEffect(() => {
    if (!payData) return;

    const { source, data: PayData } = JSON.parse(decodeURIComponent(payData));

    const getItems = sessionStorage.getItem('presentData');
    const parseData = JSON.parse(getItems);

    setInputValue(parseData?.inputValue);
    setData(parseData?.data);

    setTimeout(() => {
      setPaymentReturn({
        status: source === 'payment-fail' ? 'fail' : 'success',
        data: PayData,
      });
    }, 500);
  }, [payData]);

  useEffect(() => {
    if (!Present) return;

    setData(Present);
  }, [Present]);

  /* ===== RENDER ===== */
  return (
    <>
      <PresentDetailPresenter
        data={Present}
        isLoading={isLoading}
        onDownload={handleDownload}
        inputValue={inputValue}
        onChange={handleChange}
        onSubmit={handleSubmit}
        selected={selected}
        onSelect={setSelected}
        card={Card}
        onExtension={handleExtension}
      />
    </>
  );
};

export default PresentDetailContainer;
